import { BalanceData, GearTarget, isAura, isBarracks, isBeam, isHex, isMortar } from "../data/balance";
import { GearItem, GearOwner } from "./gear";

/**
 * Where a Mythic effect does anything (§9.4). An effect lives in the sim as a hook (`LevelSim.hasMythic`, resolved by
 * id there), and each hook sits on a code path only some tower archetypes take — Twin Shells repeats the splash, so a
 * tower without splash never triggers it. These rules mirror the hooks so the Gear screen can say "does nothing on
 * Lightning" instead of letting the player wear a dead effect; a change to a hook in sim/systems/combat.ts or
 * levelSim.ts must change the matching rule here (mythic.test.ts pins the table).
 */
export const MYTHIC_ARCHER_CHAIN = "archerChain";
export const MYTHIC_CANNON_DOUBLE_SPLASH = "cannonDoubleSplash";
export const MYTHIC_FROST_FREEZE = "frostFreeze";
export const MYTHIC_BASE_TROOP_CAP = "baseTroopCap";
export const MYTHIC_FIRE_SPREAD = "fireSpread";
export const MYTHIC_LIGHTNING_CHAIN = "lightningChain";
export const MYTHIC_BALLISTA_PIERCE = "ballistaPierce";
export const MYTHIC_POISON_SHRED = "poisonShred";
export const MYTHIC_BEACON_RANGE = "beaconRange";
export const MYTHIC_MORTAR_CLUSTER = "mortarCluster";
export const MYTHIC_BARRACKS_FOURTH = "barracksFourth";
export const MYTHIC_ARCANE_OVERCHARGE = "arcaneOvercharge";
export const MYTHIC_HEX_DEEP_CURSE = "hexDeepCurse";

/** True when Mythic effect `effect` (an index into `gear.mythicEffects`) acts for `owner` in a level. */
export function mythicEffectActive(balance: BalanceData, effect: number, owner: GearOwner): boolean {
  const def = balance.gear.mythicEffects[effect];
  if (def === undefined) return false;
  // The troop cap is read from the base's gear when the level starts (levelSim constructor).
  if (owner.target === GearTarget.Base) return def.id === MYTHIC_BASE_TROOP_CAP;
  const a = balance.towers[owner.towerIndex];
  if (a === undefined || a.isBase) return false;
  // Wider Beacon widens the aura (rangeOf), the one hook a tower that never fires takes.
  if (def.id === MYTHIC_BEACON_RANGE) return isAura(a);
  // The TOWERS_V2 hooks each sit on their own tower's mechanic (sim/systems/mortar.ts, beam.ts, hex.ts, sim/barracks.ts).
  if (def.id === MYTHIC_MORTAR_CLUSTER) return isMortar(a);
  if (def.id === MYTHIC_BARRACKS_FOURTH) return isBarracks(a);
  if (def.id === MYTHIC_ARCANE_OVERCHARGE) return isBeam(a);
  if (def.id === MYTHIC_HEX_DEEP_CURSE) return isHex(a);
  // A Barracks never fires a shot: the shot hooks below never run for it.
  if (isBarracks(a)) return false;
  // Every other tower hook runs from fireTowers, which skips auras and towers that never fire.
  const fires = !isAura(a) && a.fireIntervalTicks > 0;
  // A piercing shot returns before the Splitting Arrows and splash code; chain towers skip Splitting Arrows.
  // A Mortar's shell and an Arcane beam leave fireTowers before those too (systems/mortar.ts, beam.ts).
  const plainShot = fires && a.pierceCount <= 0 && !isMortar(a) && !isBeam(a);
  switch (def.id) {
    case MYTHIC_ARCHER_CHAIN: return plainShot && a.chainTargets <= 1;
    // Frost's splash only chills (combat.ts), so a second pass would do nothing: Twin Shells needs a damaging splash.
    case MYTHIC_CANNON_DOUBLE_SPLASH: return plainShot && a.splashRadius.isPositive() && a.damage.isPositive();
    // Deep Freeze rides on the slow in applyHit, which every hit (pierce and chain included) goes through.
    case MYTHIC_FROST_FREEZE: return fires && a.slowFraction.isPositive() && a.slowDurationTicks > 0;
    // Wildfire passes on burns this tower type lit (applyHit lights them on every hit, pierce and chain included).
    case MYTHIC_FIRE_SPREAD: return fires && a.burnDamagePerSecond.isPositive() && a.burnDurationTicks > 0;
    // Forked Bolt adds hops to fireChain, which a piercing shot never reaches and a chain-less tower never calls.
    case MYTHIC_LIGHTNING_CHAIN: return plainShot && a.chainTargets > 1;
    case MYTHIC_BALLISTA_PIERCE: return fires && a.pierceCount > 0;
    // Deep Rot raises the shred cap in applyHit.
    case MYTHIC_POISON_SHRED: return fires && a.shredPerHit.isPositive() && a.shredMax > 0;
    default: return false;
  }
}

/** True when the item carries a Mythic effect and that effect acts on `owner` (false for no effect). */
export function mythicActiveOn(balance: BalanceData, item: GearItem, owner: GearOwner): boolean {
  return item.mythicEffect >= 0 && mythicEffectActive(balance, item.mythicEffect, owner);
}
